import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timestamp, Timezone, TypeGuard } from '@principle-theorem/shared';
import { flow, isNull, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { convertExactId, getExactSourceDate } from '../../util/helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_DOCUMENTS_SOURCE_TYPE = 'patientDocument';

export const PATIENT_DOCUMENTS_SOURCE_ENTITY: ISourceEntity = SourceEntity.init(
  {
    metadata: {
      label: 'Patient Documents',
      description: ``,
      idPrefix: PATIENT_DOCUMENTS_SOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  }
);

export interface IExactPatientDocument {
  id: string | null;
  patient_id: string;
  source_id: string;
  date: string;
  title: string;
  source_path: string;
  user_initials: string;
  type: string;
}

export interface IExactPatientDocumentTranslations {
  date: Timestamp;
}

export interface IExactPatientDocumentFilters {
  patientId: string;
}

function isExactPatientDocument(
  item: unknown,
  logError: boolean
): item is IExactPatientDocument {
  return TypeGuard.interface<IExactPatientDocument>(
    {
      id: [isString, isNull],
      patient_id: isString,
      source_id: isString,
      date: isString,
      title: isString,
      source_path: isString,
      user_initials: isString,
      type: isString,
    },
    logError
  )(item);
}

const PATIENT_DOCUMENTS_SOURCE_QUERY = `
SELECT
  patientId::TEXT AS patient_id,
  sourceId::TEXT AS source_id,
  NULLIF(id::TEXT, '') AS id,
  title,
  sourcePath AS source_path,
  userinitials AS user_initials,
  contactType AS type,
  date
FROM convcontact
`;

export type ExactPatientDocument = ISourceEntityTypes<
  IExactPatientDocument,
  IExactPatientDocumentTranslations,
  IExactPatientDocumentFilters
>;

export class PatientDocumentsSourceEntity extends BaseSourceEntity<ExactPatientDocument> {
  sourceEntity = PATIENT_DOCUMENTS_SOURCE_ENTITY;
  entityResourceType = PATIENT_DOCUMENTS_SOURCE_TYPE;
  sourceQuery = PATIENT_DOCUMENTS_SOURCE_QUERY;
  verifySourceFn = isExactPatientDocument;
  override defaultOffsetSize = 50000;

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([transformPatientDocumentResults]);

  getSourceRecordId(data: IExactPatientDocument): string {
    return data.id ?? data.source_id;
  }

  getSourceLabel(record: IExactPatientDocument): string {
    return `${record.patient_id} ${this.getSourceRecordId(record)}`;
  }

  translate(
    data: IExactPatientDocument,
    timezone: Timezone
  ): IExactPatientDocumentTranslations {
    return {
      date: getExactSourceDate(data.date, timezone),
    };
  }

  getFilterData(data: IExactPatientDocument): IExactPatientDocumentFilters {
    return { patientId: data.patient_id };
  }
}

function transformPatientDocumentResults(
  rows: IExactPatientDocument[]
): IExactPatientDocument[] {
  return rows.map((row) => ({
    ...row,
    id: row.id ? convertExactId(row.id) : convertExactId(row.source_id),
    patient_id: convertExactId(row.patient_id),
    source_id: convertExactId(row.source_id),
  }));
}
